'use client';

import { useEffect, useMemo, useState } from 'react';
import config from 'config';
import { useGetPaymentMethodsQuery } from 'services/paymentMethodsApi';

import { Box, useTheme, VStack } from '@chakra-ui/react';

import { handlePageScroll } from 'utils';

import { ScreenTypeEnum } from 'types';

import { Divider, Loader } from 'ui';
import { AddCardBox, ErrorModal, PaymentCCardItem } from './components';

interface PaymentListProps {
  onCardSelect?: (cardId: number | null) => void;
  onSubmitMethod?: (cardId: number | null) => void;
  errorMsg: string;
  isPaymentLoading: boolean;
  onTypeHandler: (type: ScreenTypeEnum) => void;
  onClearError?: () => void;
  isMembershipFlow?: boolean;
  initialId?: number | null;
  handlePayment?: () => void;
}

const {
  payment: { title, description }
} = config;

const PaymentList = ({
  onCardSelect = () => {},
  onSubmitMethod = () => {},
  errorMsg,
  isPaymentLoading,
  onClearError = () => {},
  onTypeHandler,
  isMembershipFlow = false,
  initialId = null,
  handlePayment = () => {}
}: PaymentListProps) => {
  const { data: clientAccounts, isLoading: isCardLoading } =
    useGetPaymentMethodsQuery('', { refetchOnMountOrArgChange: true });
  const {
    colors: {
      palette: {
        components: { paymentList }
      }
    }
  } = useTheme();
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  const isCardsExist = clientAccounts && clientAccounts?.length > 0;

  const handleClearError = () => {
    if (errorMsg) {
      onClearError();
    }
  };

  const onSelectCard = (id: number) => () => {
    if (selectedCardId === id && !isMembershipFlow) {
      setSelectedCardId(null);
      onCardSelect(null);
    } else {
      setSelectedCardId(id);
      onCardSelect(id);
      onSubmitMethod(id);
      handleClearError();
    }
  };

  const handleSubmit = () => {
    handleClearError();
    handlePayment();
    if (selectedCardId !== null) {
      onCardSelect(selectedCardId);
      onSubmitMethod(selectedCardId);
    }
  };

  const onAnotherCard = () => {
    onTypeHandler(ScreenTypeEnum.newCardForm);
  };

  const clearSelectedCard = () => {
    if (clientAccounts && clientAccounts?.length > 1) {
      onTypeHandler(ScreenTypeEnum.cardList);
    } else {
      onTypeHandler(ScreenTypeEnum.newCardForm);
    }
    setSelectedCardId(null);
    onCardSelect(null);
  };

  useEffect(() => {
    handlePageScroll(isPaymentLoading);
  }, [isPaymentLoading]);

  useEffect(() => {
    if (initialId !== null) {
      setSelectedCardId(initialId);
    }
  }, [initialId, setSelectedCardId]);

  useEffect(
    () => () => {
      handleClearError();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const accountList = useMemo(
    () => (Array.isArray(clientAccounts) ? clientAccounts : []),
    [clientAccounts]
  );

  return (
    <Box>
      {isCardLoading && <Loader absolute />}
      <VStack gap={isMembershipFlow ? 0 : '5px'}>
        {accountList?.map((item, index) => (
          <PaymentCCardItem
            key={item.id}
            card={item}
            onSelectCard={onSelectCard(item.id)}
            isActive={item.id === selectedCardId}
            isMembershipFlow={isMembershipFlow}
            isLast={(accountList?.length ?? 1) - 1 === index}
          />
        ))}
        <Box>
          {isMembershipFlow && (
            <Divider bg={paymentList.itemDivider} h='1px' border='none' />
          )}
          <AddCardBox
            label={isCardsExist ? 'Add Another Credit Card' : 'Add Credit Card'}
            onSelectCard={onAnotherCard}
            borderRadius={isMembershipFlow ? '0px' : '6px'}
          />
        </Box>
      </VStack>

      <ErrorModal
        isOpen={!!errorMsg}
        apiErrorText={errorMsg}
        onClose={handleClearError}
        onPaymentAgain={handleSubmit}
        onAnotherCard={clearSelectedCard}
        title={title}
        description={description}
      />
    </Box>
  );
};

export default PaymentList;
